// Image compression utility - compresses images by quality (not cropping)
// Uses Canvas API to reduce file size while maintaining aspect ratio.
//
// Mobile note: FileReader.readAsDataURL duplicates the entire file in memory
// and decoding a 12–48 MP camera photo often triggers "not enough memory" on
// Android. Prefer createImageBitmap (subsampling) and object URLs over data URLs.

const RASTER_IMAGE_RE = /^image\/(jpeg|jpg|png|webp)$/i;

/** Some Android cameras omit MIME type; still try to downscale by extension. */
function isLikelyRasterPhoto(file) {
  if (!file) return false;
  if (RASTER_IMAGE_RE.test(file.type || '')) return true;
  const n = (file.name || '').toLowerCase();
  return /\.(jpe?g|png|webp)$/i.test(n);
}

function jpegOrPngMime(file) {
  const t = (file.type || '').toLowerCase();
  return t === 'image/png' ? 'image/png' : 'image/jpeg';
}

/**
 * Aggressively downscale a photo for PlantNet / identify flows.
 * Always returns JPEG to keep size predictable and avoid huge PNGs.
 * Tries createImageBitmap first so the browser can decode at reduced resolution.
 *
 * @param {File} file
 * @param {number} [maxSide=1600] longest edge cap
 * @param {number} [quality=0.82]
 * @returns {Promise<File>}
 */
export async function downscaleImageForPlantIdentification(file, maxSide = 1600, quality = 0.82) {
  if (!file || !isLikelyRasterPhoto(file)) return file;

  const maxW = maxSide;
  const maxH = maxSide;
  const outName = `${(file.name || 'photo').replace(/\.[^.]+$/i, '') || 'photo'}.jpg`;

  if (typeof createImageBitmap === 'function') {
    try {
      let bm = await createImageBitmap(file, {
        resizeWidth: maxW,
        resizeQuality: 'medium',
      });
      if (bm.height > maxH) {
        bm.close();
        bm = await createImageBitmap(file, {
          resizeHeight: maxH,
          resizeQuality: 'medium',
        });
      }
      const canvas = document.createElement('canvas');
      canvas.width = bm.width;
      canvas.height = bm.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('No canvas context');
      ctx.drawImage(bm, 0, 0);
      bm.close();

      const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error('Failed to compress image'))),
          'image/jpeg',
          quality
        );
      });
      return new File([blob], outName, { type: 'image/jpeg', lastModified: Date.now() });
    } catch (e) {
      console.warn('[downscaleImageForPlantIdentification] createImageBitmap path failed', e);
    }
  }

  return compressImageWithObjectUrl(file, maxW, maxH, quality, outName, 'image/jpeg');
}

/**
 * Compress an image file by reducing quality
 * @param {File} file - The image file to compress
 * @param {number} maxWidth - Maximum width (default: 1920)
 * @param {number} maxHeight - Maximum height (default: 1920)
 * @param {number} quality - Quality 0-1 (default: 0.8)
 * @returns {Promise<File>} - Compressed file
 */
export const compressImage = (file, maxWidth = 1920, maxHeight = 1920, quality = 0.8) => {
  if (!RASTER_IMAGE_RE.test(file.type || '')) {
    return Promise.resolve(file);
  }

  if (typeof createImageBitmap === 'function') {
    return (async () => {
      try {
        let bm = await createImageBitmap(file, {
          resizeWidth: maxWidth,
          resizeQuality: 'high',
        });
        if (bm.height > maxHeight) {
          bm.close();
          bm = await createImageBitmap(file, {
            resizeHeight: maxHeight,
            resizeQuality: 'high',
          });
        }
        const canvas = document.createElement('canvas');
        canvas.width = bm.width;
        canvas.height = bm.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('No canvas context');
        ctx.drawImage(bm, 0, 0);
        bm.close();
        const mime = jpegOrPngMime(file);
        const blob = await new Promise((resolve, reject) => {
          canvas.toBlob(
            (b) => (b ? resolve(b) : reject(new Error('Failed to compress image'))),
            mime,
            mime === 'image/jpeg' ? quality : undefined
          );
        });
        return new File([blob], file.name, { type: blob.type || mime, lastModified: Date.now() });
      } catch (e) {
        console.warn('[compressImage] createImageBitmap failed, using object-URL fallback', e);
      }
      return compressImageWithObjectUrl(file, maxWidth, maxHeight, quality, null, null);
    })();
  }

  return compressImageWithObjectUrl(file, maxWidth, maxHeight, quality, null, null);
};

/**
 * Decode via blob URL (avoids a full-file base64 string from FileReader).
 * @param {string|null} forcedName — if set, used for output File name
 * @param {string|null} outputMime — if set, forces toBlob type (e.g. image/jpeg for identify)
 */
function compressImageWithObjectUrl(file, maxWidth, maxHeight, quality, forcedName, outputMime) {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      let width = img.width;
      let height = img.height;

      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = Math.max(1, Math.round(width * ratio));
        height = Math.max(1, Math.round(height * ratio));
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to compress image'));
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);

      const mime = outputMime || jpegOrPngMime(file);
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Failed to compress image'));
            return;
          }
          const name = forcedName || file.name;
          resolve(
            new File([blob], name, {
              type: blob.type || mime,
              lastModified: Date.now(),
            })
          );
        },
        mime,
        mime === 'image/jpeg' || mime === 'image/webp' ? quality : undefined
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load image'));
    };

    img.src = objectUrl;
  });
}

/**
 * Compress multiple images
 * @param {File[]} files - Array of image files
 * @param {number} maxWidth - Maximum width
 * @param {number} maxHeight - Maximum height
 * @param {number} quality - Quality 0-1
 * @returns {Promise<File[]>} - Array of compressed files
 */
export const compressImages = async (files, maxWidth = 1920, maxHeight = 1920, quality = 0.8) => {
  const compressedFiles = await Promise.all(
    files.map(file => compressImage(file, maxWidth, maxHeight, quality))
  );
  return compressedFiles;
};

/**
 * Get file size in readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};



















